import styles from "./Budgets.module.scss";
import React, {Fragment, useContext, useMemo, useState} from "react";
import {HeaderMenu, HeaderMenuAction, HeaderMenuLeft} from "../../common/HeaderMenu";
import {useDispatch, useSelector} from "react-redux";
import {
  listConfigSelector,
  rxShowBudgetCategoryEditor,
  rxToggleTransactionsVisibility,
  rxToPeriod
} from "../../slices/budgetCategoriesSlice";
import useFormat from "../../util/useFormat";
import {MODE_EDIT, PORTFOLIO_TRANSACTION_TYPE_BUY} from "../../codes";
import {useTranslation} from "react-i18next";
import {listToMap, round, sortTransactionsAsc} from "../../util/util";
import clsx from "clsx";
import ColoredAmount from "../../common/ColoredAmount";
import {BudgetCategoryEditor} from "./budget-cateogry-editor/BudgetCategoryEditor";
import {BudgetsFab} from "./BudgetsFab";
import {selectAllAccounts} from "../../slices/accountsSlice";
import {selectAllMileageLogs} from "../../slices/mileageLogsSlice";
import {selectAllCars} from "../../slices/carsSlice";
import {selectAllPortfolioTransactions} from "../../slices/portfolioSlice";
import moment from "moment";
import {AppContext} from "../../App";
import useProject from "../../util/useProject";
import {BudgetBarIn, BudgetBarOut} from "../../common/BudgetBar";

const makeGroup = (categories, currentMonth) => {
  const g = {};

  categories.forEach(c => {
    const category = c.category;

    if (!g[category.groupName]) {
      g[category.groupName] = {
        groupName: category.groupName,
        categories: {}
      };
    }

    if (!g[category.groupName].categories[category.id]) {
      g[category.groupName].categories[category.id] = {
        category,
        budgetCat: currentMonth === -1 ? null : c.budgetCat,
        amountPlanned: 0,
        transactions: []
      };
    }

    g[category.groupName].categories[category.id].amountPlanned += c.budgetCat.amount;
    g[category.groupName].categories[category.id].transactions.push(...c.transactions);
  });

  Object.values(g).forEach(group => {
    group.categories = Object.values(group.categories).toSorted((a, b) => a.category.name.localeCompare(b.category.name));
  });

  return Object.values(g).toSorted((a, b) => a.groupName.localeCompare(b.groupName));
};

const Budgets = () => {
  const {formatAmount} = useFormat();

  const dispatch = useDispatch();

  const accountsList = useSelector(selectAllAccounts);
  const mileageLogsList = useSelector(selectAllMileageLogs);
  const portfolioTransactionsList = useSelector(selectAllPortfolioTransactions);
  const carsList = useSelector(selectAllCars);

  const accounts = useMemo(() => listToMap(accountsList, "id"), [accountsList]);
  const mileageLogs = useMemo(() => listToMap(mileageLogsList.filter(ml => ml.transaction), "transaction"), [mileageLogsList]);
  const portfolioTransactions = useMemo(() => listToMap(portfolioTransactionsList.filter(sp => sp.transaction), "transaction"), [portfolioTransactionsList]);
  const cars = useMemo(() => listToMap(carsList, "id"), [carsList]);

  const {currentYear, currentMonth, showTransactions} = useSelector(listConfigSelector);

  const [toolbarShown, setToolbarShown] = useState(false);

  const {t} = useTranslation("budgets");

  const toggleToolbar = () => setToolbarShown(prev => !prev);

  const {periods} = useContext(AppContext).budget;

  const currentPeriod = periods.find(p => p.month === currentMonth && p.year === currentYear);

  const outGroups = useMemo(() => {
    if (!currentPeriod) {
      return [];
    } else {
      return makeGroup(currentPeriod.outCategories, currentMonth);
    }
  }, [currentMonth, currentPeriod]);

  const inGroups = useMemo(() => {
    if (!currentPeriod) {
      return [];
    } else {
      return makeGroup(currentPeriod.inCategories, currentMonth);
    }
  }, [currentMonth, currentPeriod]);

  const transactionsNoCategory = currentPeriod.noCategories;

  const nonBudgetAmount = transactionsNoCategory.reduce((a, b) => a + (b.amount * b.rate), 0);

  let totalTransactions = transactionsNoCategory.length;

  totalTransactions += outGroups
    .flatMap(group => group.categories)
    .flatMap(tc => tc.transactions)
    .length;

  totalTransactions += inGroups
    .flatMap(group => group.categories)
    .flatMap(tc => tc.transactions)
    .length;

  const subTitle = (currentMonth === -1 ? currentYear : moment({
    year: currentYear,
    month: currentMonth,
    day: 1
  }).format("YYYY, MMM"));

  return (
    <div className={styles.budgets}>
      <HeaderMenu
        title={t("header_title_budgets")}
        subTitle={subTitle}
        onTitleClick={toggleToolbar}
        endIcon={
          <span className={clsx(styles.menuTitleIcon, "material-icons")}>
            {toolbarShown ? "arrow_drop_up" : "arrow_drop_down"}
          </span>
        }
      >
        <HeaderMenuLeft>
          <HeaderMenuAction
            icon={showTransactions ? "visibility_off" : "visibility"}
            label={t(showTransactions ? "menu_hide_transactions" : "menu_show_transactions")}
            onClick={() => {
              dispatch(rxToggleTransactionsVisibility());
            }}
          />
        </HeaderMenuLeft>
      </HeaderMenu>

      <div className={styles.budgetsContainer}>
        <div className={clsx(styles.toolbar, toolbarShown ? styles.shown : null, "custom-scrollbar")}>
          {periods.map(period => {
            const isCurrentMonth = currentMonth === period.month;
            const isCurrentYear = currentYear === period.year;
            const isYear = period.month === -1;

            return <Fragment key={period.year + "-" + period.month}>
              {period.month === -1 && <div className={styles.yearSeparator}/>}
              <div
                className={clsx(styles.toolbarPeriod, isCurrentMonth && isCurrentYear ? styles.active : null, isYear ? styles.year : null)}
                onClick={() => {
                  setToolbarShown(false);

                  dispatch(rxToPeriod(period));
                }}
                role="link"
                tabIndex={period.month}
              >
                <div
                  className={styles.value}>{moment(period.start).format(period.month === -1 ? "YYYY" : "YYYY, MMMM")}</div>

                <ColoredAmount amount={period.amountFilled !== 0 ? period.amountFilled - period.amountPlanned : 0}>
                  {formatAmount(period.amountFilled)}
                </ColoredAmount>

                <div className={styles.amountPlanned}>{formatAmount(period.amountPlanned)}</div>
              </div>
            </Fragment>;
          })}
        </div>

        <div className={clsx(styles.categories, "custom-scrollbar")}>
          <div className={styles.budgetBars}>
            <div className={styles.categoriesTitle}>
              <h1>
                {(currentMonth === -1 ? currentYear : moment({
                  year: currentYear,
                  month: currentMonth,
                  day: 1
                }).format("YYYY, MMMM"))}
                {" "}
                ({totalTransactions})
              </h1>

              <div className={styles.totalFilled}>
                <ColoredAmount
                  amount={currentPeriod.amountFilled !== 0 ? currentPeriod.amountFilled - currentPeriod.amountPlanned : 0}>
                  {formatAmount(currentPeriod.amountFilled)}
                </ColoredAmount>

                &nbsp;/&nbsp;

                {formatAmount(currentPeriod.amountPlanned)}</div>
            </div>

            <div className={styles.budgetBarTitle}>
              <div className={styles.title}>{t("budget_incomes")}</div>
              <div className={styles.numbers}>
                {currentPeriod && <>{formatAmount(Math.abs(currentPeriod.amountInFilled))} / {formatAmount(Math.abs(currentPeriod.amountInPlanned))}</>}
              </div>
            </div>

            <BudgetBarIn amountFilled={currentPeriod.amountInFilled} amountPlanned={currentPeriod.amountInPlanned}/>

            <div className={styles.budgetBarTitle}>
              <div className={styles.title}>{t("budget_expenses")}</div>
              <div className={styles.numbers}>
                {currentPeriod && <>{formatAmount(Math.abs(currentPeriod.amountOutFilled))} / {formatAmount(Math.abs(currentPeriod.amountOutPlanned))}</>}
              </div>
            </div>

            <BudgetBarOut amountFilled={currentPeriod.amountOutFilled} amountPlanned={currentPeriod.amountOutPlanned}/>
          </div>

          {inGroups.map(group => {
            return <div key={group.groupName} className={styles.groupContainer}>
              <div className={styles.groupCategories}>
                {group.categories.map(groupCategory => <Category
                  key={groupCategory.category.id}
                  groupCategory={groupCategory}
                  accounts={accounts}
                  mileageLogs={mileageLogs}
                  portfolioTransactions={portfolioTransactions}
                  cars={cars}
                  out={false}
                />)}
              </div>
            </div>;
          })}

          {outGroups.map(group => {
            const categories = group.categories;

            const groupTransactions = categories.flatMap(tc => tc.transactions);

            const groupAmountPlanned = categories.reduce((a, b) => a + b.amountPlanned, 0);
            const groupAmountFilled = groupTransactions.reduce((a, b) => a + (b.amount * b.rate), 0);

            return <div key={group.groupName} className={styles.groupContainer}>
              <div className={styles.group}>
                <div className={styles.groupTitle}>{group.groupName} ({groupTransactions.length})</div>

                <div className={styles.groupChart}>
                  <BudgetBarOut amountFilled={groupAmountFilled} amountPlanned={groupAmountPlanned}
                                background="#bbbbbbAA"/>
                </div>

                <div className={styles.groupAmounts}>
                  <div className={styles.groupAmount}>
                    {formatAmount(groupAmountFilled)}
                  </div>
                  <div className={styles.groupAmount}>
                    {formatAmount(groupAmountPlanned)}
                  </div>
                </div>
              </div>

              <div className={styles.groupCategories}>
                {categories.map(groupCategory => <Category
                  key={groupCategory.category.id}
                  groupCategory={groupCategory}
                  accounts={accounts}
                  mileageLogs={mileageLogs}
                  portfolioTransactions={portfolioTransactions}
                  cars={cars}
                />)}
              </div>
            </div>;
          })}

          <div className={styles.groupContainer}>
            {outGroups.length > 0 && <hr/>}

            <div className={styles.groupCategories}>
              <div className={clsx(styles.category, styles.nonBudget)}>
                <div className={styles.categoryDetails}>
                  <div className={styles.categoryColor} style={{background: "#555555"}}>
                    <span className="material-icons" style={{color: "#FFFFFF"}}>help_outline</span>
                  </div>
                  <div className={styles.categoryName}>
                    <div>{t("category_non_budget")} ({transactionsNoCategory.length})</div>
                  </div>
                  <div className={styles.categoryAmountFilled}>
                    {formatAmount(nonBudgetAmount)}
                  </div>
                  <div className={styles.categoryAmountPlanned}>
                    -
                  </div>
                </div>
              </div>

              <CategoryTransactions
                transactions={transactionsNoCategory.toSorted(sortTransactionsAsc)}
                accounts={accounts}
                mileageLogs={mileageLogs}
                portfolioTransactions={portfolioTransactions}
                cars={cars}
              />
            </div>
          </div>
        </div>
      </div>

      <BudgetCategoryEditor/>

      {currentMonth >= 0 && <BudgetsFab/>}
    </div>
  );
};

const Category = ({groupCategory, accounts, cars, mileageLogs, portfolioTransactions, out = true}) => {
  const {runWithExpiration} = useProject();

  const {formatAmount} = useFormat();

  const dispatch = useDispatch();

  const showCategoryEditor = (mode, category) => dispatch(rxShowBudgetCategoryEditor({
    mode,
    category,
    valueDate: category.valueDate
  }));

  const transactionCategory = groupCategory.category;

  const [background, color] = transactionCategory.color.split("|");

  const amountFilled = round(groupCategory.transactions.reduce((a, b) => a + (b.amount * b.rate), 0), 2);
  const amountPlanned = groupCategory.amountPlanned;

  let coloredAmount = 0;

  if (amountFilled > amountPlanned && amountPlanned > 0) {
    coloredAmount = 1;
  } else if (amountFilled < amountPlanned && amountPlanned < 0) {
    coloredAmount = -1;
  }

  return <div className={clsx(styles.category)}>
    <div className={clsx(styles.categoryDetails, groupCategory.budgetCat ? "hover-selectable" : null)}
         onClick={groupCategory.budgetCat ? runWithExpiration(() => {
           showCategoryEditor(MODE_EDIT, groupCategory.budgetCat);
         }) : null}
         role="button"
         tabIndex="0"
    >
      <div className={styles.categoryColor} style={{background}}>
        <span className="material-icons" style={{color}}>{transactionCategory.icon}</span>
      </div>
      <div className={styles.categoryName}>
        <div>{transactionCategory.name} ({groupCategory.transactions.length})</div>
      </div>

      <div className={styles.categoryChart}>
        {out && <BudgetBarOut amountFilled={amountFilled} amountPlanned={groupCategory.amountPlanned}
                              background="#bbbbbbAA"/>}
        {!out && <BudgetBarIn amountFilled={amountFilled} amountPlanned={groupCategory.amountPlanned}
                              background="#bbbbbbAA"/>}
      </div>

      <div className={styles.categoryAmounts}>
        <div className={styles.categoryAmountFilled}>
          <ColoredAmount amount={coloredAmount}>{formatAmount(amountFilled)}</ColoredAmount>
        </div>
        <div className={styles.categoryAmountPlanned}>
          {formatAmount(groupCategory.amountPlanned)}
        </div>
      </div>
    </div>

    <CategoryTransactions
      transactions={groupCategory.transactions.toSorted(sortTransactionsAsc)}
      accounts={accounts}
      mileageLogs={mileageLogs}
      portfolioTransactions={portfolioTransactions}
      cars={cars}
    />
  </div>;
};

const CategoryTransactions = ({transactions, accounts, cars, mileageLogs, portfolioTransactions}) => {
  const {formatAmount, formatNumber, formatDate} = useFormat();
  const {showTransactions} = useSelector(listConfigSelector);

  if (!showTransactions || transactions.length === 0) {
    return null;
  }

  return <div className={styles.transactions}>
    {transactions.map(transaction => {
      const account = accounts[transaction.account];

      let payeeComponent = <span>{transaction.payeeDisplay || transaction.payee}</span>;

      let note = transaction.note;
      let mileageLog = mileageLogs[transaction.id];
      let portfolioTransaction = portfolioTransactions[transaction.id];

      if (mileageLog) {
        const car = cars[mileageLog.car];

        const [background, color] = car.color.split("|");

        payeeComponent = <span className={clsx(styles.tag, styles.carName)} style={{background, color}}>
          {car.name}
        </span>;

        const parts = [];

        if (mileageLog.distance) {
          parts.push(formatNumber(mileageLog.odometer, "#T##0D##") + " " + car.odometerUnit + " (+" + formatNumber(mileageLog.distance, "#T##0D##") + " " + car.odometerUnit + ")");
        } else {
          parts.push(formatNumber(mileageLog.odometer, "#T##0D##") + " " + car.odometerUnit);
        }

        parts.push(formatNumber(mileageLog.quantity) + " " + car.quantityUnit + " @ " + formatAmount(mileageLog.price));

        if (mileageLog.conso) {
          parts.push(formatNumber(mileageLog.conso, "#T##0D000") + " " + car.consoUnit);
        }

        note = parts.join(" | ");
      } else if (portfolioTransaction) {
        const positiveNegativeStyle = portfolioTransaction.type === PORTFOLIO_TRANSACTION_TYPE_BUY
          ? styles.portfolioTransactionPositive
          : styles.portfolioTransactionNegative;

        payeeComponent = <span className={clsx(styles.tag, positiveNegativeStyle)}>
        {portfolioTransaction.type === PORTFOLIO_TRANSACTION_TYPE_BUY ? "BUY" : "SELL"} {portfolioTransaction.symbol}
      </span>;

        note = formatNumber(portfolioTransaction.quantity, "#T##0D##") + " @ " + formatAmount(portfolioTransaction.price)
          + (portfolioTransaction.note ? " | " + portfolioTransaction.note : "");
      }

      return <div key={transaction.id} className={styles.transaction}>
        <div className={styles.name}>
          {payeeComponent}

          {note && <span className={styles.note}>{note}</span>}
        </div>
        <div className={styles.amount}>
          <ColoredAmount className={styles.amount} amount={transaction.amount}>
            {formatAmount(transaction.amount, account.currency)}
          </ColoredAmount>
        </div>
        <div className={styles.valueDate}>
          {formatDate(transaction.valueDate)}
        </div>
      </div>;
    })}
  </div>;
};

export default Budgets;
